
/* ==================================================
   HORIZON — PRODUCT LIGHTBOX NAVIGATION
   Infinite arrows + swipe + live thumbnail sync
================================================== */

(() => {
  'use strict';

  if (window.__productZoomGalleryFinalInitialized) return;
  window.__productZoomGalleryFinalInitialized = true;

  const GALLERY_SELECTOR = 'zoom-dialog';
  const DIALOG_SELECTOR = '.dialog-zoomed-gallery__dialog';
  const TRACK_SELECTOR = '.dialog-zoomed-gallery';
  const THUMBNAILS_SELECTOR = '.dialog-thumbnails-list';
  const THUMB_SELECTOR = '.dialog-thumbnails-list__thumbnail';

  function createArrow(direction) {
    const button = document.createElement('button');

    button.type = 'button';
    button.className =
      `product-zoom-gallery-arrow product-zoom-gallery-arrow--${direction}`;

    button.setAttribute(
      'aria-label',
      direction === 'prev' ? 'Previous product image' : 'Next product image'
    );

    button.innerHTML = direction === 'prev'
      ? '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
      : '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    return button;
  }

  function initGallery(gallery) {
    if (gallery.dataset.zoomGalleryFinalReady === 'true') return;

    const dialog = gallery.querySelector(DIALOG_SELECTOR);
    const track = gallery.querySelector(TRACK_SELECTOR);
    const thumbnailsContainer = gallery.querySelector(THUMBNAILS_SELECTOR);

    if (!dialog || !track || !thumbnailsContainer) return;

    const previousExisting = dialog.querySelector(
      '.product-zoom-gallery-arrow--prev'
    );
    const nextExisting = dialog.querySelector(
      '.product-zoom-gallery-arrow--next'
    );

    const previous = previousExisting || createArrow('prev');
    const next = nextExisting || createArrow('next');

    if (!previous.isConnected) dialog.appendChild(previous);
    if (!next.isConnected) dialog.appendChild(next);

    gallery.dataset.zoomGalleryFinalReady = 'true';

    let syncFrame = 0;
    let scrollEndTimer = 0;

    function getSlides() {
      return Array.from(track.children).filter(
        (element) => element instanceof HTMLElement
      );
    }

    function getThumbnails() {
      return Array.from(
        thumbnailsContainer.querySelectorAll(THUMB_SELECTOR)
      );
    }

    function getCurrentIndex() {
      const slides = getSlides();

      if (!slides.length) return 0;

      const trackRect = track.getBoundingClientRect();
      const trackCenter = trackRect.left + trackRect.width / 2;

      let activeIndex = 0;
      let smallestDistance = Infinity;

      slides.forEach((slide, index) => {
        const rect = slide.getBoundingClientRect();
        const center = rect.left + rect.width / 2;
        const distance = Math.abs(center - trackCenter);

        if (distance < smallestDistance) {
          smallestDistance = distance;
          activeIndex = index;
        }
      });

      return activeIndex;
    }

    function updateThumbnailSelection(index, scrollThumbnail = true) {
      const thumbnails = getThumbnails();

      thumbnails.forEach((thumbnail, thumbnailIndex) => {
        const selected = thumbnailIndex === index;

        if (thumbnail.getAttribute('aria-selected') !== String(selected)) {
          thumbnail.setAttribute('aria-selected', String(selected));
        }
      });

      if (scrollThumbnail && thumbnails[index]) {
        const thumbnail = thumbnails[index];
        const containerRect = thumbnailsContainer.getBoundingClientRect();
        const thumbRect = thumbnail.getBoundingClientRect();

        if (
          thumbRect.left < containerRect.left ||
          thumbRect.right > containerRect.right
        ) {
          thumbnail.scrollIntoView({
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
              ? 'auto'
              : 'smooth',
            block: 'nearest',
            inline: 'center'
          });
        }
      }
    }

    function updateArrowVisibility() {
      const slides = getSlides();
      const hasMultipleSlides = slides.length > 1;

      previous.hidden = !hasMultipleSlides;
      next.hidden = !hasMultipleSlides;

      /* Never disable arrows: they wrap around infinitely. */
      previous.disabled = false;
      next.disabled = false;
    }

    function goToIndex(index, behavior = 'smooth') {
      const slides = getSlides();

      if (!slides.length) return;

      const wrappedIndex = ((index % slides.length) + slides.length) % slides.length;
      const target = slides[wrappedIndex];

      if (!target) return;

      target.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 'auto'
          : behavior,
        block: 'nearest',
        inline: 'center'
      });

      updateThumbnailSelection(wrappedIndex);
    }

    function syncActiveThumbnail() {
      if (!dialog.open) return;

      const activeIndex = getCurrentIndex();

      updateThumbnailSelection(activeIndex);
      updateArrowVisibility();
    }

    function scheduleSync() {
      if (syncFrame) cancelAnimationFrame(syncFrame);

      syncFrame = requestAnimationFrame(() => {
        syncFrame = 0;
        syncActiveThumbnail();
      });

      window.clearTimeout(scrollEndTimer);
      scrollEndTimer = window.setTimeout(syncActiveThumbnail, 100);
    }

    previous.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      goToIndex(getCurrentIndex() - 1);
    });

    next.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      goToIndex(getCurrentIndex() + 1);
    });

    /* Native Horizon thumbnail clicks select the correct media.
       This listener keeps the visual selection immediately in sync. */
    thumbnailsContainer.addEventListener('click', (event) => {
      const thumbnail = event.target.closest(THUMB_SELECTOR);
      if (!thumbnail) return;

      const index = getThumbnails().indexOf(thumbnail);

      if (index >= 0) {
        updateThumbnailSelection(index);
      }

      window.setTimeout(scheduleSync, 60);
    });

    /* Sync while the user swipes or scrolls between images. */
    track.addEventListener('scroll', scheduleSync, { passive: true });

    /* Keyboard navigation while the lightbox is open. */
    dialog.addEventListener('keydown', (event) => {
      if (!dialog.open) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goToIndex(getCurrentIndex() - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        goToIndex(getCurrentIndex() + 1);
      }
    });

    /* Ensure the current image and thumbnail are aligned whenever opened. */
    const observer = new MutationObserver(() => {
      if (dialog.open) {
        updateArrowVisibility();
        window.setTimeout(syncActiveThumbnail, 50);
      }
    });

    observer.observe(dialog, {
      attributes: true,
      attributeFilter: ['open']
    });

    dialog.addEventListener('close', () => {
      window.clearTimeout(scrollEndTimer);

      if (syncFrame) {
        cancelAnimationFrame(syncFrame);
        syncFrame = 0;
      }
    });

    updateArrowVisibility();
  }

  function initAllGalleries() {
    document.querySelectorAll(GALLERY_SELECTOR).forEach(initGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllGalleries, { once: true });
  } else {
    initAllGalleries();
  }

  if (window.customElements) {
    customElements.whenDefined('zoom-dialog').then(initAllGalleries);
  }

  /* Supports galleries inserted dynamically by Shopify sections. */
  const pageObserver = new MutationObserver(() => {
    initAllGalleries();
  });

  pageObserver.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();
